import React from 'react';
import type { EvaluationItem, TupleMatchingConfig, PairDef } from '../../../../../types/course';
import FloatingLabelPill from '../shared/FloatingLabelPill';
import AnswerPill from '../shared/AnswerPill';
import SectionHeader from '../shared/SectionHeader';
import AddItemCTA from '../shared/AddItemCTA';
import TrashButton from '../shared/TrashButton';
import { DiagonalArrowIcon } from '../shared/SolarIcon';
import { uid } from '../shared/tokens';

interface ItemEditorProps {
  item: Extract<EvaluationItem, { type: 'tuple_matching' }>;
  onChange: (patch: Partial<Extract<EvaluationItem, { type: 'tuple_matching' }>>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

export default function TupleMatchingItem({ item, onChange, onDelete, readOnly }: ItemEditorProps) {
  const { question, pairs } = item.config;

  const updateConfig = (patch: Partial<TupleMatchingConfig>) => {
    onChange({ config: { ...item.config, ...patch } });
  };

  const updatePair = (id: string, patch: Partial<PairDef>) => {
    updateConfig({ pairs: pairs.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  };

  const removePair = (id: string) => {
    if (pairs.length <= 2) return;
    updateConfig({ pairs: pairs.filter((p) => p.id !== id) });
  };

  const addPair = () => {
    const newPair: PairDef = { id: uid('pair'), left: '', right: '' };
    updateConfig({ pairs: [...pairs, newPair] });
  };

  return (
    <div className="flex flex-col gap-[31px] w-full">
      <SectionHeader title="Matching" />
      <FloatingLabelPill
        label="Question"
        value={question}
        onChange={(v) => updateConfig({ question: v })}
        placeholder="Instructions for matching"
        size="xl"
        readOnly={readOnly}
        trailingIcon={!readOnly && <TrashButton onClick={onDelete} ariaLabel="Delete question" />}
      />
      <div className="flex flex-col gap-3">
        {pairs.map((p) => (
          <div
            key={p.id}
            className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-3"
          >
            <div className="flex-1">
              <AnswerPill
                value={p.left}
                onChange={(v) => updatePair(p.id, { left: v })}
                placeholder="Left item"
                state="selected"
                readOnly={readOnly}
              />
            </div>
            <div className="flex items-center justify-center md:px-2" aria-hidden="true">
              <DiagonalArrowIcon size={22} />
            </div>
            <div className="flex-1">
              <AnswerPill
                value={p.right}
                onChange={(v) => updatePair(p.id, { right: v })}
                placeholder="Right item"
                readOnly={readOnly}
                onRemove={!readOnly && pairs.length > 2 ? () => removePair(p.id) : undefined}
              />
            </div>
          </div>
        ))}
      </div>
      {!readOnly && <AddItemCTA label="Add Pair" onClick={addPair} />}
    </div>
  );
}
